import {
  saveToLocalStorage,
  loadFromLocalStorage,
  updateLocalStorage,
  deleteFromLocalStorage
} from '../utils/localStorage';

import {
  fakeJobSeekers,
  fakeEmployers,
  fakeCompanies,
  fakeJobs,
  fakeReviews,
  fakeAIMatches,
  fakeApplications
} from './mockData';

// Simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Initialize fake data on first load
export const initializeFakeData = async () => {
  await delay(300);

  if (!loadFromLocalStorage('jobSeekers')) {
    saveToLocalStorage('jobSeekers', fakeJobSeekers);
  }

  if (!loadFromLocalStorage('employers')) {
    saveToLocalStorage('employers', fakeEmployers);
  }

  if (!loadFromLocalStorage('companies')) {
    saveToLocalStorage('companies', fakeCompanies);
  }

  if (!loadFromLocalStorage('jobs')) {
    saveToLocalStorage('jobs', fakeJobs);
  }

  if (!loadFromLocalStorage('reviews')) {
    saveToLocalStorage('reviews', fakeReviews);
  }

  if (!loadFromLocalStorage('aiMatches')) {
    saveToLocalStorage('aiMatches', fakeAIMatches);
  }

  if (!loadFromLocalStorage('applications')) {
    saveToLocalStorage('applications', fakeApplications);
  }

  if (!loadFromLocalStorage('jobPosts')) {
    saveToLocalStorage('jobPosts', []);
  }

  return { success: true };
};

// User Profile APIs
export const getUserProfile = async (userId) => {
  await delay();

  const currentUser = loadFromLocalStorage('currentUser');
  if (!currentUser || !currentUser.role) {
    return { success: false, error: 'User not found' };
  }

  if (currentUser.role === 'jobseeker') {
    const jobSeekers = loadFromLocalStorage('jobSeekers') || [];
    const profile = jobSeekers.find(js => js.userId === userId);
    return { success: true, profile };
  } else if (currentUser.role === 'employer') {
    const employers = loadFromLocalStorage('employers') || [];
    const profile = employers.find(emp => emp.userId === userId);
    return { success: true, profile };
  }

  return { success: false, error: 'Invalid role' };
};

export const updateUserProfile = async (userId, profileData) => {
  await delay();

  const currentUser = loadFromLocalStorage('currentUser');

  if (currentUser.role === 'jobseeker') {
    const jobSeekers = loadFromLocalStorage('jobSeekers') || [];
    const index = jobSeekers.findIndex(js => js.userId === userId);

    if (index !== -1) {
      jobSeekers[index] = { ...jobSeekers[index], ...profileData };
      saveToLocalStorage('jobSeekers', jobSeekers);
      return { success: true, profile: jobSeekers[index] };
    } else {
      // Create new profile
      const newProfile = { id: `js${Date.now()}`, userId, ...profileData, role: 'jobseeker' };
      jobSeekers.push(newProfile);
      saveToLocalStorage('jobSeekers', jobSeekers);
      return { success: true, profile: newProfile };
    }
  } else if (currentUser.role === 'employer') {
    const employers = loadFromLocalStorage('employers') || [];
    const index = employers.findIndex(emp => emp.userId === userId);

    if (index !== -1) {
      employers[index] = { ...employers[index], ...profileData };
      saveToLocalStorage('employers', employers);
      return { success: true, profile: employers[index] };
    } else {
      const newProfile = { id: `emp${Date.now()}`, userId, ...profileData, role: 'employer' };
      employers.push(newProfile);
      saveToLocalStorage('employers', employers);
      return { success: true, profile: newProfile };
    }
  }

  return { success: false, error: 'Invalid role' };
};

// Company APIs
export const getAllCompanies = async () => {
  await delay();
  const companies = loadFromLocalStorage('companies') || [];
  return { success: true, companies };
};

export const getCompanyById = async (companyId) => {
  await delay();
  const companies = loadFromLocalStorage('companies') || [];
  const company = companies.find(c => c.id === companyId);
  return { success: true, company };
};

export const updateCompany = async (companyId, companyData) => {
  await delay();
  const companies = loadFromLocalStorage('companies') || [];
  const index = companies.findIndex(c => c.id === companyId);

  if (index !== -1) {
    companies[index] = { ...companies[index], ...companyData };
    saveToLocalStorage('companies', companies);
    return { success: true, company: companies[index] };
  }

  return { success: false, error: 'Company not found' };
};

// Candidate APIs
export const getAllCandidates = async () => {
  await delay();
  const candidates = loadFromLocalStorage('jobSeekers') || [];
  return { success: true, candidates };
};

export const getCandidateById = async (candidateId) => {
  await delay();
  const candidates = loadFromLocalStorage('jobSeekers') || [];
  const candidate = candidates.find(c => c.id === candidateId);
  return { success: true, candidate };
};

// Review APIs
export const getReviewsByTarget = async (targetId) => {
  await delay();
  const reviews = loadFromLocalStorage('reviews') || [];
  const targetReviews = reviews.filter(r => r.targetId === targetId);
  return { success: true, reviews: targetReviews };
};

export const addReview = async (reviewData) => {
  await delay();
  const reviews = loadFromLocalStorage('reviews') || [];
  const newReview = {
    id: `rev${Date.now()}`,
    ...reviewData,
    date: new Date().toISOString().split('T')[0],
    helpful: 0
  };
  reviews.push(newReview);
  saveToLocalStorage('reviews', reviews);
  return { success: true, review: newReview };
};

// AI Match APIs
export const getAIMatchesByJobSeeker = async (jobSeekerId) => {
  await delay();
  const matches = loadFromLocalStorage('aiMatches') || [];
  const userMatches = matches.filter(m => m.jobSeekerId === jobSeekerId);
  return { success: true, matches: userMatches };
};

export const getAIMatchById = async (matchId) => {
  await delay();
  const matches = loadFromLocalStorage('aiMatches') || [];
  const match = matches.find(m => m.id === matchId);
  return { success: true, match };
};

// Job APIs
export const getAllJobs = async () => {
  await delay();
  const jobs = loadFromLocalStorage('jobs') || [];
  return { success: true, jobs };
};

export const getJobById = async (jobId) => {
  await delay();
  const jobs = loadFromLocalStorage('jobs') || [];
  const job = jobs.find(j => j.id === jobId);
  return { success: true, job };
};

// Application APIs
export const getApplicationsByJobSeeker = async (jobSeekerId) => {
  await delay();
  const applications = loadFromLocalStorage('applications') || [];
  const userApplications = applications.filter(a => a.jobSeekerId === jobSeekerId);
  return { success: true, applications: userApplications };
};

export const submitApplication = async (applicationData) => {
  await delay();
  const applications = loadFromLocalStorage('applications') || [];
  const newApplication = {
    id: `app${Date.now()}`,
    ...applicationData,
    status: 'submitted',
    appliedDate: new Date().toISOString().split('T')[0]
  };
  applications.push(newApplication);
  saveToLocalStorage('applications', applications);
  return { success: true, application: newApplication };
};

// Settings APIs
export const getUserSettings = async (userId) => {
  await delay();
  const settings = loadFromLocalStorage(`settings_${userId}`) || {
    notifications: true,
    emailUpdates: true,
    profileVisibility: 'public',
    theme: 'light'
  };
  return { success: true, settings };
};

export const updateUserSettings = async (userId, settingsData) => {
  await delay();
  saveToLocalStorage(`settings_${userId}`, settingsData);
  return { success: true, settings: settingsData};
};

// Job Post APIs
export const createJobPost = async (jobPostData) => {
  await delay();
  const jobPosts = loadFromLocalStorage('jobPosts') || [];
  const newJobPost = {
    id: `job${Date.now()}`,
    ...jobPostData,
    createdAt: new Date().toISOString()
  };
  jobPosts.push(newJobPost);
  saveToLocalStorage('jobPosts', jobPosts);
  return { success: true, jobPost: newJobPost };
};

export const getJobPostsByEmployer = async (employerId) => {
  await delay();
  const jobPosts = loadFromLocalStorage('jobPosts') || [];
  const employerPosts = jobPosts.filter(jp => jp.employerId === employerId);
  return { success: true, jobPosts: employerPosts };
};

export const getAllJobPosts = async () => {
  await delay();
  const jobPosts = loadFromLocalStorage('jobPosts') || [];
  return { success: true, jobPosts };
};

export const getApprovedJobPosts = async () => {
  await delay();
  const jobPosts = loadFromLocalStorage('jobPosts') || [];
  const approvedPosts = jobPosts.filter(jp => jp.status === 'approved');
  return { success: true, jobPosts: approvedPosts };
};

export const updateJobPostStatus = async (jobPostId, status) => {
  await delay();
  const jobPosts = loadFromLocalStorage('jobPosts') || [];
  const index = jobPosts.findIndex(jp => jp.id === jobPostId);

  if (index !== -1) {
    jobPosts[index].status = status;
    jobPosts[index].updatedAt = new Date().toISOString();
    saveToLocalStorage('jobPosts', jobPosts);
    return { success: true, jobPost: jobPosts[index] };
  }

  return { success: false, error: 'Job post not found' };
};

export const deleteJobPost = async (jobPostId) => {
  await delay();
  const jobPosts = loadFromLocalStorage('jobPosts') || [];
  const filteredPosts = jobPosts.filter(jp => jp.id !== jobPostId);
  saveToLocalStorage('jobPosts', filteredPosts);
  return { success: true };
};
